import { orgService } from "@/api/org/orgService";
import { userService } from "@/api/user/userService";
import PandaLoadingIndicator from "@/components/PandaLoadingIndicator";
import { SETTINGS, USER_INFO } from "@/constants/queryKeys";
import { SettingsContext } from "@/context/SettingContext";
import { useAuth } from "@/hooks/useAuth";
import { queryKeys } from "@/utils/helper/queryKeys";
import { useQuery } from "@tanstack/react-query";
import React from "react";

const preloadCriticalSidebarIcons = async () => {
  await Promise.all([
    import("@/components/PandaCustomIcon/CustomIcons/CartIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/FileText"),
    import("@/components/PandaCustomIcon/CustomIcons/LayoutDashboardIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/LayoutSidebarIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/RandomIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/SettingsIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/SquareCheckBigIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/TicketIcon"),
    import("@/components/PandaCustomIcon/CustomIcons/TrophyIcon"),
  ]);
};

export const SettingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { token } = useAuth();
  const [areSidebarIconsReady, setAreSidebarIconsReady] = React.useState(false);

  // Use /core/bootstrap instead of /user/setting
  // This bypasses the admin portal gate, so portal-off tenants can boot
  // The adapter in orgService reshapes the response to match the old shape
  const { data, isFetched, isFetching } = useQuery({
    queryFn: () => orgService.getBootstrapForSettings(),
    queryKey: queryKeys(SETTINGS).list({ token }),
    staleTime: 1 * 60 * 1000,
    enabled: !!token, // Only fetch when token exists
    refetchOnWindowFocus: true,
  });
  const { data: settingsData } = data || {};
  const { settings, tenant } = settingsData || {};

  const {
    data: userData,
    isFetched: isUserFetched,
    isFetching: isUserFetching,
  } = useQuery({
    queryFn: () => userService.getUserInfo(),
    queryKey: queryKeys(USER_INFO).list({ token }),
    enabled: !!token,
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const { data: userInfo } = userData || {};

  React.useEffect(() => {
    let isMounted = true;

    if (!token) {
      setAreSidebarIconsReady(false);
      return;
    }

    setAreSidebarIconsReady(false);

    void preloadCriticalSidebarIcons()
      .then(() => {
        if (isMounted) {
          setAreSidebarIconsReady(true);
        }
      })
      .catch(() => {
        if (isMounted) {
          // Do not block app boot if any optional icon chunk fails to preload.
          setAreSidebarIconsReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  const isBootstrapping =
    !token ||
    !areSidebarIconsReady ||
    !isFetched ||
    !isUserFetched ||
    (isFetching && !isFetched) ||
    (isUserFetching && !isUserFetched);

  if (isBootstrapping) {
    return (
      <PandaLoadingIndicator
        message="Preparing your workspace"
        subMessage="Loading tenant settings, user profile, and navigation..."
      />
    );
  }

  return (
    <SettingsContext.Provider
      value={{
        settings: settings,
        tenantInfo: tenant,
        userInfo,
        isLoading: isFetching || isUserFetching,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
